import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { colors } from 'chime-ui/system';

import { ButtonSpinnerIcon } from '../Icons';

const StyledButton = styled.button`
  display: block;
  min-width: 80px;
  width: 100%;
  height: 3rem;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  text-shadow: none;
  line-height: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: none;
  transition: all 0.3s ease 0s;
  padding: 0.75rem 1rem;
  color: ${colors['content-inverse']};
  background-color: ${colors['bg-action-primary']};
  border: none;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
  box-sizing: border-box;

  &:hover,
  &:focus {
    outline: none;
    background-color: ${colors['bg-action-primary-hover']};
  }

  &:active {
    outline: none;
    background-color: ${colors['accent-green400']};
  }

  ${props =>
    (props.disabled || props.loading) &&
    css`
      color: ${colors['disabled-primary']};
      background-color: ${colors['bg-tertiary']};
      pointer-events: none;
    `};
`;

export const Button = ({ loading, children, ...props }) => (
  <StyledButton {...props}>
    {loading ? <ButtonSpinnerIcon /> : children}
  </StyledButton>
);

Button.propTypes = {
  loading: PropTypes.bool,
  children: PropTypes.node,
};

// styled(Button) doesn't propagate the `as` prop down to the base
// StyledButton, so we need to use styled(StyledButton) here and use different
// button components as applicable within SpacedButton. See
// https://github.com/styled-components/styled-components/issues/2375#issuecomment-465375626
// for detail.
const SpacedButtonLink = styled(StyledButton)`
  margin-top: 1.5rem;
`;

const SpacedButtonButton = styled(Button)`
  margin-top: 1.5rem;
`;

export const SpacedButton = props => {
  return props.as === 'a' ? (
    <SpacedButtonLink {...props} />
  ) : (
    <SpacedButtonButton {...props} />
  );
};

export const AltButton = styled(StyledButton)`
  background-color: ${colors['content-light']};
  box-shadow: 0 4px 12px rgba(51, 51, 51, 0.16);
`;

export const SecondaryButton = styled(StyledButton)`
  color: ${colors.darkGreen};
  background: none;
`;

export const ghostButtonMixin = css`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
`;

export const GhostButton = styled.button`
  ${ghostButtonMixin}
`;

export const UnderlinedGhostButton = styled.button`
  ${ghostButtonMixin};
  text-decoration: underline;
  font-weight: 500;
`;

export const UnderlinedBoldGhostButton = styled.button`
  ${ghostButtonMixin};
  text-decoration: underline;
  font-weight: 700;
`;

export const GhostLink = styled.a`
  ${ghostButtonMixin}
  text-decoration: underline;
`;

export const GreenLink = styled.a`
  ${ghostButtonMixin}
  text-decoration: underline;
  color: ${colors['content-action-primary']};
`;

export const IconButton = styled.button.attrs(({ icon }) => ({
  children: icon || null,
}))`
  ${ghostButtonMixin};
  &:disabled {
    opacity: 0.5;
    cursor: auto;
  }
  &:focus {
    outline: 2px solid ${colors.darkGreen};
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;
